(() => {
  'use strict';
  if (window.__vmProductPage) return;
  window.__vmProductPage = true;

  const initialized = new WeakSet();
  const stickyStates = new WeakMap();
  const interest = new Map();
  const MIRROR_STYLE_PROPERTIES = [
    'appearance', 'display', 'align-items', 'justify-content', 'gap', 'box-sizing',
    'min-height', 'height', 'padding', 'color', 'background', 'background-color',
    'background-image', 'border', 'border-radius', 'box-shadow', 'font-family',
    'font-size', 'font-weight', 'font-style', 'line-height', 'letter-spacing',
    'text-align', 'text-decoration', 'text-transform', 'white-space', 'cursor', 'pointer-events',
    'opacity', 'transition'
  ];
  const INTERACTIVE_SELECTOR = 'button, a, input[type="button"], input[type="submit"], [role="button"]';
  const EASYSELL_SELECTORS = [
    '#es-popup-button',
    '.es-popup-button-overwrite',
    '[data-easysell-button]',
    '[data-easy-sell-button]',
    '[data-easysell-popup-button]',
    '[class*="easysell"]:is(button, a, input, [role="button"])',
    '[class*="easy-sell"]:is(button, a, input, [role="button"])',
    '[class*="easysell"] button',
    '[class*="easy-sell"] button'
  ];
  let scheduled = false;
  let documentObserver;
  let layoutObserver;
  let observedLayoutKey = '';

  const setText = (node, value) => {
    if (node && node.textContent !== value) node.textContent = value;
  };

  function productRoot() {
    return document.querySelector('product-info.vm-p-main');
  }

  function isVisible(node) {
    return Boolean(node && node.isConnected && node.getClientRects().length && getComputedStyle(node).display !== 'none');
  }

  function isNativeAddToCart(node) {
    if (!node || node.matches('.product-form__submit, [data-vm-native-add-to-cart]')) return true;
    const form = node.closest('form[data-type="add-to-cart-form"]');
    return Boolean(form && (node.type === 'submit' || node.name === 'add' || node.matches('[name="add"]')));
  }

  function isSourceDisabled(source) {
    const nativeSubmit = productRoot()?.querySelector('.product-form__submit');
    return Boolean(
      source.disabled ||
      source.getAttribute('aria-disabled') === 'true' ||
      source.classList.contains('disabled') ||
      nativeSubmit?.disabled ||
      getComputedStyle(source).pointerEvents === 'none'
    );
  }

  function validSource(node) {
    return Boolean(
      node &&
      node.matches(INTERACTIVE_SELECTOR) &&
      isVisible(node) &&
      !node.closest('[data-vm-product-sticky]') &&
      !isNativeAddToCart(node)
    );
  }

  function sourceFromConfiguredSelector(root) {
    const selector = (root.dataset.vmCodButtonSelector || '').trim();
    if (!selector) return null;

    try {
      const localMatch = Array.from(root.querySelectorAll(selector)).find(validSource);
      if (localMatch) return localMatch;
      return Array.from(document.querySelectorAll(selector)).find(validSource) || null;
    } catch (error) {
      return null;
    }
  }

  function sourceFromEasySell(root) {
    for (const selector of EASYSELL_SELECTORS) {
      const match = Array.from(root.querySelectorAll(selector)).find(validSource);
      if (match) return match;
    }
    return null;
  }

  function findEasySellSource(root) {
    return sourceFromConfiguredSelector(root) || sourceFromEasySell(root);
  }

  function copyPresentation(source, mirror) {
    const computed = getComputedStyle(source);
    MIRROR_STYLE_PROPERTIES.forEach(property => {
      mirror.style.setProperty(property, computed.getPropertyValue(property));
    });
    mirror.style.setProperty('width', '100%');
    mirror.style.setProperty('max-width', '100%');
    mirror.style.setProperty('margin', '0');
  }

  function cleanseMirror(mirror) {
    [mirror, ...mirror.querySelectorAll('*')].forEach(node => {
      node.removeAttribute('id');
      Array.from(node.attributes).forEach(attribute => {
        const name = attribute.name.toLowerCase();
        if (
          name.startsWith('data-') ||
          name.startsWith('on') ||
          ['action', 'form', 'formaction', 'formenctype', 'formmethod', 'formnovalidate', 'formtarget', 'href', 'name', 'target'].includes(name)
        ) {
          node.removeAttribute(attribute.name);
        }
      });
      Array.from(node.classList || []).forEach(className => {
        if (/easysell|easy-sell|es-popup|popup-button-overwrite/i.test(className)) node.classList.remove(className);
      });
    });

    if (mirror.matches('button, input')) mirror.type = 'button';
    if (mirror.matches('a')) {
      mirror.removeAttribute('href');
      mirror.setAttribute('role', 'button');
      mirror.tabIndex = 0;
    }
    if (mirror.matches('[role="button"]') && !mirror.hasAttribute('tabindex')) mirror.tabIndex = 0;
    mirror.removeAttribute('aria-controls');
    mirror.removeAttribute('aria-describedby');
    mirror.removeAttribute('aria-labelledby');
    mirror.removeAttribute('aria-haspopup');
  }

  function sourceSignature(source) {
    return [
      source.outerHTML,
      source.value || '',
      source.disabled ? 'disabled' : '',
      source.getAttribute('aria-disabled') || '',
      productRoot()?.querySelector('.product-form__submit')?.disabled ? 'variant-disabled' : ''
    ].join('|');
  }

  function renderMirror(sticky, source) {
    const host = sticky.querySelector('[data-vm-sticky-button]');
    if (!host) return;

    const mirror = source.cloneNode(true);
    cleanseMirror(mirror);
    copyPresentation(source, mirror);
    mirror.classList.add('vm-p-sticky__proxy');

    const syncDisabledState = () => {
      const disabled = isSourceDisabled(source);
      if ('disabled' in mirror) mirror.disabled = disabled;
      mirror.setAttribute('aria-disabled', String(disabled));
      mirror.tabIndex = disabled ? -1 : 0;
    };

    const activateSource = event => {
      event.preventDefault();
      event.stopPropagation();
      if (source.isConnected && !isSourceDisabled(source)) source.click();
    };

    mirror.addEventListener('click', activateSource);
    mirror.addEventListener('keydown', event => {
      if (!mirror.matches('button, input') && (event.key === 'Enter' || event.key === ' ')) activateSource(event);
    });
    syncDisabledState();
    host.replaceChildren(mirror);
  }

  function setStickyVisibility(sticky, visible) {
    if (sticky.hidden === !visible) return;
    sticky.hidden = !visible;
  }

  function measureLayout(root = productRoot()) {
    const nav = document.querySelector('.vm-bottom-nav');
    const sticky = root?.querySelector('[data-vm-product-sticky]');
    const navHeight = isVisible(nav) ? Math.ceil(nav.getBoundingClientRect().height) : 0;
    const stickyHeight = isVisible(sticky) ? Math.ceil(sticky.getBoundingClientRect().height) : 0;
    document.body.style.setProperty('--vmp-bottom-nav-height', `${navHeight}px`);
    document.body.style.setProperty('--vmp-sticky-height', `${stickyHeight}px`);
  }

  function observeLayout(root) {
    const nav = document.querySelector('.vm-bottom-nav');
    const sticky = root?.querySelector('[data-vm-product-sticky]');
    const targets = [nav, sticky].filter(isVisible);
    const key = targets.map(node => `${node.tagName}:${node.className}`).join('|');

    if (window.ResizeObserver && key !== observedLayoutKey) {
      layoutObserver?.disconnect();
      layoutObserver = new ResizeObserver(() => measureLayout());
      targets.forEach(node => layoutObserver.observe(node));
      observedLayoutKey = key;
    }
    measureLayout(root);
  }

  function synchronizeSticky(root) {
    const sticky = root.querySelector('[data-vm-product-sticky]');
    if (!sticky) {
      observeLayout(root);
      return;
    }

    const source = findEasySellSource(root);
    if (!source) {
      stickyStates.delete(sticky);
      sticky.querySelector('[data-vm-sticky-button]')?.replaceChildren();
      setStickyVisibility(sticky, false);
      observeLayout(root);
      return;
    }

    const signature = sourceSignature(source);
    const state = stickyStates.get(sticky);
    if (!state || state.source !== source || state.signature !== signature) {
      renderMirror(sticky, source);
      stickyStates.set(sticky, { source, signature });
    }
    setStickyVisibility(sticky, true);
    observeLayout(root);
  }

  function synchronize() {
    const root = productRoot();
    if (!root) {
      measureLayout(null);
      return;
    }

    root.querySelectorAll('[data-vm-interest-handle]').forEach(container => {
      const handle = container.dataset.vmInterestHandle;
      if (!interest.has(handle)) interest.set(handle, Math.floor(Math.random() * 4));
      const value = interest.get(handle);
      const counter = container.querySelector('[data-vm-interest-count]');
      const noun = container.querySelector('[data-vm-interest-noun]');
      if (counter && counter.textContent !== String(value)) counter.textContent = String(value);
      setText(noun, value === 1 || value === 0 ? 'osoba' : 'osobe');
    });

    const quantity = root.querySelector('.quantity__input');
    const submit = root.querySelector('.product-form__submit');
    const stock = root.querySelector('[data-vm-stock]');
    if (quantity && stock) {
      const tracked = stock.dataset.tracked === 'true';
      const deny = stock.dataset.policy !== 'continue';
      const physical = Math.max(0, Number(stock.dataset.quantity) || 0);
      const cartQty = Math.max(0, Number(quantity.dataset.cartQuantity) || 0);
      const ruleMax = quantity.dataset.max == null ? Infinity : Number(quantity.dataset.max) - cartQty;
      const cap = tracked && deny ? Math.max(0, Math.min(physical - cartQty, ruleMax)) : ruleMax;
      const minimum = Math.max(1, Number(quantity.min) || 1);
      const step = Math.max(1, Number(quantity.step) || 1);
      if (Number.isFinite(cap) && cap >= minimum) {
        if (quantity.max !== String(cap)) quantity.max = String(cap);
        const value = Math.max(minimum, Math.min(Number(quantity.value) || minimum, cap));
        quantity.value = String(minimum + Math.floor((value - minimum) / step) * step);
      } else if (!Number.isFinite(cap) && quantity.hasAttribute('max')) {
        quantity.removeAttribute('max');
      }
      const soldOut = stock.dataset.available === 'false';
      const blockedByStock = tracked && deny && cap < minimum;
      if (quantity.disabled !== (soldOut || blockedByStock)) quantity.disabled = soldOut || blockedByStock;
      if (submit && blockedByStock && !submit.disabled) {
        submit.disabled = true;
        submit.dataset.vmStockLocked = 'true';
      } else if (submit?.dataset.vmStockLocked && !blockedByStock && !soldOut) {
        submit.disabled = false;
        delete submit.dataset.vmStockLocked;
      }
    }

    const price = root.querySelector('[data-vm-current-price-label]');
    const label = price?.dataset.vmCurrentPriceLabel;
    if (label) root.querySelectorAll('[data-vm-sticky-price]').forEach(node => setText(node, label));
    const count = Number(quantity?.value) || 1;
    root.querySelectorAll('[data-vm-sticky-quantity]').forEach(node => {
      setText(node, `${count} ${count === 1 ? 'par' : count < 5 ? 'para' : 'pari'}`);
    });
    if (label) root.querySelectorAll('[data-vm-wish]').forEach(button => {
      if (button.dataset.price !== label) button.dataset.price = label;
    });

    synchronizeSticky(root);
    window.__vmWinter?.refreshWishButtons?.();
  }

  function schedule() {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      synchronize();
    });
  }

  function businessDate(date, days) {
    const result = new Date(date);
    let remaining = days;
    while (remaining > 0) {
      result.setUTCDate(result.getUTCDate() + 1);
      if (result.getUTCDay() !== 0 && result.getUTCDay() !== 6) remaining -= 1;
    }
    return result;
  }

  function showDeliveryDates() {
    const parts = new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/Belgrade', year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(new Date());
    const values = Object.fromEntries(parts.map(part => [part.type, part.value]));
    const today = new Date(Date.UTC(Number(values.year), Number(values.month) - 1, Number(values.day), 12));
    const format = date => new Intl.DateTimeFormat('sr-Latn-RS', { day: 'numeric', month: 'long', timeZone: 'UTC' }).format(date);
    document.querySelectorAll('[data-vm-delivery-date]').forEach(node => {
      node.textContent = `${format(businessDate(today, 4))} – ${format(businessDate(today, 6))}`;
    });
  }

  function initialize() {
    const root = productRoot();
    if (root && !initialized.has(root)) {
      initialized.add(root);
      const observer = new MutationObserver(schedule);
      observer.observe(root, { childList: true, subtree: true, characterData: true, attributes: true });
    }
    if (document.body && !documentObserver) {
      documentObserver = new MutationObserver(schedule);
      documentObserver.observe(document.body, { childList: true, subtree: true });
    }
    showDeliveryDates();
    schedule();
  }

  document.addEventListener('change', event => {
    if (event.target.matches('.vm-p-main .quantity__input')) schedule();
  });
  document.addEventListener('product-info:loaded', initialize);
  document.addEventListener('shopify:section:load', initialize);
  window.addEventListener('resize', schedule, { passive: true });
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.variantChange, schedule);
    subscribe(PUB_SUB_EVENTS.quantityUpdate, schedule);
    subscribe(PUB_SUB_EVENTS.cartUpdate, schedule);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
  else initialize();
})();
